import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { downloadCsvTemplate, parseCsvData } from "../../../shared/utils/csvParser";
import { NewUser } from "../../../shared/utils/userValidation";
import { profileClient } from "@/shared/api/clients";
import { useOrgId } from "../../../shared/auth/orgContext";

export interface BulkUserUploadProps {
  onUsersUploaded: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  stationId?: string;
  csvUsers: NewUser[];
  setCsvUsers: React.Dispatch<React.SetStateAction<NewUser[]>>;
  resetPage: () => void;
}

const BulkUserUpload: React.FC<BulkUserUploadProps> = ({
  onUsersUploaded,
  onError,
  onSuccess,
  stationId,
  csvUsers,
  setCsvUsers,
  resetPage,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const organizationId = useOrgId();
  const effectiveStationId = stationId ?? organizationId;

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      onError("Please upload a CSV file");
      setCsvUsers([]);
      setCsvFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseCsvData(text);
      if (result.errors.length > 0) {
        onError(`CSV validation errors:\n${result.errors.join("\n")}`);
        setCsvUsers([]);
        resetPage();
        return;
      }

      setCsvUsers(result.users);
      resetPage();
      onSuccess(`Successfully parsed ${result.users.length} users from CSV`);
    };
    reader.readAsText(file);
  };

  const handleSubmitCsvUsers = async () => {
    if (!effectiveStationId) {
      onError("Unable to submit: station or organization ID is missing. Please refresh and try again.");
      return;
    }

    if (csvUsers.length === 0) {
      onError("No parsed users to submit");
      return;
    }

    setIsSubmitting(true);

    try {
      await profileClient.post(`/v1/stations/${effectiveStationId}/users/bulk`, {
        rows: csvUsers.map((user) => ({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          isAdmin: !!user.isAdmin,
          metadata: {
            division_name: user.department,
            job_title: user.position,
            badge_number: user.badgeNumber,
          },
        })),
      });
      onSuccess("Successfully submitted users!");
      setCsvUsers([]);
      setCsvFile(null);
      resetPage();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onUsersUploaded();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const uploadErrors = error.response?.data?.errors;
      const apiErrorMessage = error.response?.data?.error;
      if (Array.isArray(uploadErrors) && uploadErrors.length > 0) {
        onError(
          uploadErrors
            .map((entry: { message?: string }) => entry.message || "Unknown error")
            .join("\n"),
        );
      } else if (typeof apiErrorMessage === "string" && apiErrorMessage.length > 0) {
        onError(apiErrorMessage);
      } else {
        onError("Failed to add users. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveCsvFile = () => {
    setCsvFile(null);
    setCsvUsers([]);
    resetPage();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[400]}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.secondary.main, 0.1),
            color: "secondary.main",
          }}
        >
          <CloudUploadIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Bulk Invite Users
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Import multiple users at once
          </Typography>
        </Box>
      </Box>

      <Alert
        severity="info"
        icon={<WarningIcon fontSize="small" />}
        sx={{
          mb: 3,
          borderRadius: 2,
          "& .MuiAlert-message": { fontSize: "0.85rem" },
        }}
      >
        Upload a CSV file with the required columns. Download the template below
        for the correct format.
      </Alert>

      <input
        ref={fileInputRef}
        id="csv-upload"
        type="file"
        accept=".csv"
        onChange={handleCsvUpload}
        style={{ display: "none" }}
      />

      <label htmlFor="csv-upload">
        <Box
          sx={{
            border: `2px dashed ${
              csvFile
                ? theme.palette.success.main
                : alpha(theme.palette.primary.main, 0.3)
            }`,
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            bgcolor: csvFile
              ? alpha(theme.palette.success.main, 0.05)
              : alpha(theme.palette.primary.main, 0.02),
            "&:hover": {
              borderColor: csvFile
                ? theme.palette.success.main
                : theme.palette.primary.main,
              bgcolor: csvFile
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.primary.main, 0.05),
            },
            mb: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
          }}
        >
          {csvFile ? (
            <>
              <CheckCircleIcon
                sx={{ fontSize: 48, color: "success.main", mb: 1.5 }}
              />
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {csvFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to choose a different file
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: alpha(theme.palette.primary.main, 0.4),
                  mb: 1.5,
                }}
              />
              <Typography variant="body1" fontWeight={500} gutterBottom>
                Click to upload CSV file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                or drag and drop your file here
              </Typography>
            </>
          )}
        </Box>
      </label>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadCsvTemplate}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            px: 2.5,
          }}
        >
          Download Template
        </Button>

        {csvFile && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemoveCsvFile}
              disabled={isSubmitting}
              startIcon={<CancelIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              Remove File
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitCsvUsers}
              disabled={isSubmitting || !effectiveStationId}
              startIcon={<UploadIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: `0 4px 14px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                },
              }}
            >
              {isSubmitting ? "Uploading..." : "Upload CSV File"}
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default BulkUserUpload;
